/**
 * Fullstory Consent Management
 *
 * Bridges the Osano CMP (loaded via GATSBY_USE_OSANO) to Fullstory's
 * lifecycle APIs: FS('start') when MARKETING consent is granted, and
 * FS('shutdown') + anonymizeUser() when revoked.
 *
 * Two deployment modes — controlled by gatsby-ssr.js, not by this module:
 *
 *   1. Holistic (GDPR-strict)   — _fs_capture_on_startup=false in
 *      gatsby-ssr.js. Nothing captures until consent is granted and
 *      grantConsent() / FS('start') fires. Set GATSBY_FS_CONSENT_HOLISTIC=true
 *      at build time.
 *
 *   2. Soft (legacy)            — _fs_capture_on_startup=true (default).
 *      Capture starts immediately; revokeConsent() pauses it.
 *
 * This module behaves identically in both modes.
 *
 * initConsent() is opt-in at the call site (gatsby-browser.js gates it on
 * GATSBY_FS_CONSENT_HOLISTIC) because FS('shutdown') for visitors who
 * haven't accepted MARKETING is a deliberate product decision, not a
 * default. The Osano bridge below — onOsanoEvent — is generic and safe
 * to use for any consent-driven behaviour.
 */

import { FS_CONFIG } from './config';
import { anonymizeUser } from './identification';

type OsanoConsent = Record<string, 'ACCEPT' | 'DENY' | undefined>;
type OsanoHandler = (consent?: OsanoConsent) => void;

declare global {
  interface Window {
    FS: (method: string, args?: any) => void;
    Osano?: {
      cm?: {
        getConsent?: () => OsanoConsent | undefined;
        // Osano's documented subscription API. `on`/`off` are the older
        // aliases; we prefer addEventListener and fall back.
        addEventListener?: (event: string, handler: OsanoHandler) => void;
        removeEventListener?: (event: string, handler: OsanoHandler) => void;
        on?: (event: string, handler: OsanoHandler) => void;
        off?: (event: string, handler: OsanoHandler) => void;
      };
    };
    _fs_capture_on_startup?: boolean;
  }
}

// ─── State ──────────────────────────────────────────────────────────────

type ConsentListener = (granted: boolean) => void;

const listeners = new Set<ConsentListener>();
let osanoBound = false;
let lastKnownGranted: boolean | null = null;

// Osano category we care about. The CMP categorises consent — MARKETING
// is the bucket that covers session replay and product analytics.
const OSANO_CATEGORY = 'MARKETING';

/**
 * Osano CMP lifecycle commands.
 *
 * These are the queue-form names ("on" + PascalCase). Osano converts them
 * internally to their event names — onConsentSaved -> osano-cm-consent-saved
 * — so this is the same subscription, just reachable before the CMP loads.
 *
 *   ON_INITIALIZED    fires once the CMP is up, with the stored choice.
 *                     Osano replays it if you subscribe after it has already
 *                     initialised, so it is a reliable "ready" signal.
 *                     Never trigger a page reload from it — it fires on every
 *                     page load and would loop.
 *   ON_CONSENT_SAVED  the visitor actively saved a choice (Accept All, Deny
 *                     All, or Save from the preferences drawer). NOTE: Osano
 *                     also replays this one immediately (on the next animation
 *                     frame) when consent was already saved before you
 *                     subscribed. Anything acting on it must tolerate that.
 *   ON_CONSENT_CHANGED a value changed; also fires on some init paths, so
 *                     treat it as a sync signal rather than a user action.
 */
export const OSANO_COMMANDS = {
  ON_INITIALIZED: 'onInitialized',
  ON_CONSENT_SAVED: 'onConsentSaved',
  ON_CONSENT_CHANGED: 'onConsentChanged',
};

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * True when a CMP is present on the page (Osano loaded). When false, we
 * treat the visitor as having implicit consent (legacy / non-EU regions
 * where no CMP is required).
 */
export function isConsentManagementPresent(): boolean {
  return typeof window !== 'undefined' && !!window.Osano?.cm?.getConsent;
}

/**
 * Read current consent. Returns true when:
 *   - No CMP is loaded (implicit consent), OR
 *   - The CMP reports MARKETING category as accepted.
 *
 * Safe to call any time after page load; returns null only during SSR
 * where window isn't available.
 */
export function hasConsent(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isConsentManagementPresent()) return true;
  const c = window.Osano?.cm?.getConsent?.();
  return c?.[OSANO_CATEGORY] === 'ACCEPT';
}

/**
 * Subscribe to an Osano CMP lifecycle event using the CMP's own pre-load
 * queue, so registration works whether or not osano.js has executed yet.
 *
 * Osano's bundle bootstraps itself with `||` guards —
 *
 *   window.Osano = window.Osano || function () { window.Osano.data.push(arguments) };
 *   window.Osano.data = window.Osano.data || [];
 *
 * — and drains `Osano.data` on load, so anything queued beforehand is
 * replayed at init and anything queued afterwards is handled immediately.
 *
 * This is deliberately not tied to the <script id="osano"> tag: Osano can
 * also arrive via a tag manager, and anchoring to the tag would make the
 * subscription silently dead in that case.
 *
 * No-ops during SSR. Use OSANO_COMMANDS for the command name.
 *
 * @example
 *   onOsanoEvent(OSANO_COMMANDS.ON_CONSENT_SAVED, (consent) => { … });
 */
export function onOsanoEvent(command: string, handler: OsanoHandler): void {
  if (typeof window === 'undefined') return;

  const w = window as any;
  w.Osano = w.Osano || function osanoQueue() { w.Osano.data.push(arguments); };
  w.Osano.data = w.Osano.data || [];

  // Osano installs itself as a callable; fall back to the raw queue if some
  // other integration got there first and left a plain object.
  if (typeof w.Osano === 'function') w.Osano(command, handler);
  else w.Osano.data.push([command, handler]);
}

/**
 * Initialise the consent bridge. Idempotent — safe to call from
 * gatsby-browser.js / a top-level layout / useEffect.
 *
 * Effects:
 *   - Subscribes to the Osano lifecycle via the pre-load queue
 *   - Applies each reported state (FS('start') or FS('shutdown'))
 *   - Notifies registered listeners on every state change
 */
export function initConsent(): void {
  if (!FS_CONFIG.features.privacyControls) return;
  if (typeof window === 'undefined') return;
  if (osanoBound) return;
  osanoBound = true;

  const sync = () => applyConsent(hasConsent(), { silent: false });

  onOsanoEvent(OSANO_COMMANDS.ON_INITIALIZED, sync);
  onOsanoEvent(OSANO_COMMANDS.ON_CONSENT_SAVED, sync);
  onOsanoEvent(OSANO_COMMANDS.ON_CONSENT_CHANGED, sync);

  // Nothing above fires when no CMP is on the page, so fall back to implicit
  // consent — but only in soft mode, where capture is already running and
  // FS('start') is a no-op. In holistic mode capture has not started, and
  // granting before the CMP reports would record a visitor who is about to
  // decline. The trade-off is that holistic mode captures nothing on hosts
  // that never load Osano; that is the safe direction for a GDPR-strict mode.
  if (!isHolisticGating()) sync();
}

/**
 * Explicit grant — calls FS('start'). Use when wiring consent flows that
 * don't go through Osano (custom in-app consent dialogs, account settings).
 */
export function grantConsent(): void {
  applyConsent(true, { silent: false });
}

/**
 * Explicit revoke — calls FS('shutdown') and anonymises the user so any
 * subsequent capture (after a re-grant) starts under a fresh anonymous id.
 */
export function revokeConsent(): void {
  applyConsent(false, { silent: false });
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 *
 * @example
 *   const unsubscribe = onConsentChange((granted) => {
 *     if (granted) startMarketoMunchkin();
 *     else stopMarketoMunchkin();
 *   });
 *   // …
 *   unsubscribe();
 */
export function onConsentChange(callback: ConsentListener): () => void {
  listeners.add(callback);
  // Fire current state immediately so subscribers don't have to handle
  // "we haven't seen a change yet" specially.
  if (lastKnownGranted !== null) callback(lastKnownGranted);
  return () => { listeners.delete(callback); };
}

/**
 * Whether holistic consent gating is on (i.e. _fs_capture_on_startup was
 * set to false in gatsby-ssr.js). Useful for components that want to know
 * whether capture is delayed until consent.
 */
export function isHolisticGating(): boolean {
  if (typeof window === 'undefined') return false;
  return window._fs_capture_on_startup === false;
}

// ─── Internal ───────────────────────────────────────────────────────────

function applyConsent(granted: boolean, opts: { silent: boolean }): void {
  if (lastKnownGranted === granted) return;
  lastKnownGranted = granted;

  if (typeof window !== 'undefined' && window.FS) {
    window.FS(granted ? 'start' : 'shutdown');
    if (!granted) anonymizeUser();
  }

  if (!opts.silent) {
    listeners.forEach((cb) => {
      try { cb(granted); }
      catch (e) {
        if (!FS_CONFIG.isProduction) console.error('[FS consent] listener threw', e);
      }
    });
  }

  if (!FS_CONFIG.isProduction) {
    console.debug('[FS consent]', granted ? 'GRANTED → FS(start)' : 'REVOKED → FS(shutdown)+anonymize');
  }
}
